import React from "react";
import { Quote, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import BrandConstellation from "./BrandConstellation";

export default function Founder() {
  const { t } = useLang();

  return (
    <section id="founder" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            {/* Photo replaced with an artistic monogram card */}
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B101E] via-[#070B15] to-[#050810]">
              {/* Grid pattern */}
              <div className="absolute inset-0 grid-mask opacity-60" />

              {/* Halos */}
              <div className="halo w-[280px] h-[280px] bg-[#007AFF]/25 -top-16 -left-16 animate-pulse-glow" />
              <div className="halo w-[280px] h-[280px] bg-[#00F0FF]/20 -bottom-16 -right-16 animate-pulse-glow" style={{ animationDelay: "1.6s" }} />

              {/* Big monogram with brand constellation */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <BrandConstellation size={240} rotateDuration={60} />
                <span className="font-display text-[88px] leading-none font-extralight accent-gradient-text select-none -mt-4">
                  M
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#00F0FF]/30 bg-black/50 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] backdrop-blur">
                  <Sparkles className="h-3 w-3" />
                  CINO
                </span>
              </div>

              {/* Bottom label */}
              <div className="absolute bottom-5 left-5 right-5 rtl:right-auto rtl:left-auto">
                <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-md px-4 py-3">
                  <p className="font-display text-white text-sm leading-tight">{t.founder.name}</p>
                  <p className="text-[11px] text-[#00F0FF] mt-1 tracking-wide">{t.founder.role}</p>
                </div>
              </div>

              {/* Frame accent */}
              <div className="absolute -inset-px rounded-3xl pointer-events-none border border-[#00F0FF]/10" />
            </div>
            <div className="halo w-[300px] h-[300px] bg-[#007AFF]/20 -bottom-16 -left-10 -z-10" />
          </motion.div>

          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00F0FF]">
              {t.founder.eyebrow}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter text-white">
              {t.founder.title}
            </h2>

            <div className="mt-8 relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
              <Quote className="absolute -top-4 -left-2 rtl:left-auto rtl:-right-2 h-8 w-8 text-[#00F0FF]/50" />
              <p className="text-base md:text-lg text-white/85 leading-relaxed font-light">
                {t.founder.bio}
              </p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <p className="font-display text-white text-sm">{t.founder.name}</p>
                  <p className="text-xs text-[#8B95A5] mt-0.5">{t.founder.role}</p>
                </div>
                <a
                  href="#contact"
                  data-testid="founder-cta"
                  className="text-xs text-white/70 hover:text-[#00F0FF] transition-colors underline underline-offset-4 decoration-white/20"
                >
                  {t.founder.cta}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
